/**
 * Offline Canvas Storage
 * 
 * Provides localStorage-based persistence for canvas items in composer mode
 * No Supabase interaction - all operations are local only
 */

import type { CanvasItem } from '@/hooks/useCanvasItems';

const STORAGE_KEY = 'fomo-canvas-composer-items';
const STORAGE_VERSION = 1;

interface StorageData {
  version: number;
  items: CanvasItem[];
  lastModified: string;
}

/**
 * Load all canvas items from localStorage
 */
export const loadOfflineCanvasItems = (): Map<string, CanvasItem> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return new Map();
    }

    const data: StorageData = JSON.parse(stored);
    
    // Handle version migrations if needed
    if (data.version !== STORAGE_VERSION) {
      console.warn('Storage version mismatch, clearing old data');
      localStorage.removeItem(STORAGE_KEY);
      return new Map();
    }

    const itemsMap = new Map<string, CanvasItem>();
    data.items.forEach((item) => {
      // Convert date strings back to Date objects
      itemsMap.set(item.id, {
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
      });
    });

    return itemsMap;
  } catch (error) {
    console.error('Failed to load offline canvas items:', error);
    return new Map();
  }
};

/**
 * Save all canvas items to localStorage
 */
export const saveOfflineCanvasItems = (items: Map<string, CanvasItem>): boolean => {
  try {
    const data: StorageData = {
      version: STORAGE_VERSION,
      items: Array.from(items.values()),
      lastModified: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save offline canvas items:', error);
    
    // Check if it's a quota exceeded error
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded - canvas has too many items');
    }
    
    return false;
  }
};

/**
 * Add or update a single item in localStorage
 */
export const saveOfflineCanvasItem = (item: CanvasItem): boolean => {
  const items = loadOfflineCanvasItems();
  items.set(item.id, item);
  return saveOfflineCanvasItems(items);
};

/**
 * Remove a single item from localStorage
 */
export const removeOfflineCanvasItem = (itemId: string): boolean => {
  const items = loadOfflineCanvasItems();
  items.delete(itemId);
  return saveOfflineCanvasItems(items);
};

/**
 * Clear all offline canvas items
 */
export const clearOfflineCanvas = (): boolean => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Failed to clear offline canvas:', error);
    return false;
  }
};

/**
 * Get storage stats
 */
export const getOfflineCanvasStats = () => {
  const items = loadOfflineCanvasItems();
  const stored = localStorage.getItem(STORAGE_KEY);
  const sizeInBytes = stored ? new Blob([stored]).size : 0;
  
  return {
    itemCount: items.size,
    sizeInBytes,
    sizeInKB: Math.round(sizeInBytes / 1024),
    lastModified: stored ? JSON.parse(stored).lastModified : null,
  };
};

/**
 * Export canvas as JSON for backup/sharing
 */
export const exportOfflineCanvas = (): string => {
  const items = loadOfflineCanvasItems();
  const data = {
    version: STORAGE_VERSION,
    exportedAt: new Date().toISOString(),
    items: Array.from(items.values()),
  };
  return JSON.stringify(data, null, 2);
};

/**
 * Import canvas from JSON backup
 */
export const importOfflineCanvas = (jsonString: string, merge = false): boolean => {
  try {
    const data = JSON.parse(jsonString);
    
    if (!data.items || !Array.isArray(data.items)) {
      throw new Error('Invalid canvas export format');
    }

    let items: Map<string, CanvasItem>;
    
    if (merge) {
      // Merge with existing items
      items = loadOfflineCanvasItems();
      data.items.forEach((item: CanvasItem) => {
        items.set(item.id, {
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
        });
      });
    } else {
      // Replace all items
      items = new Map();
      data.items.forEach((item: CanvasItem) => {
        items.set(item.id, {
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
        });
      });
    }

    return saveOfflineCanvasItems(items);
  } catch (error) {
    console.error('Failed to import canvas:', error);
    return false;
  }
};
